import { motion } from 'framer-motion'
import { useEffect, useRef } from 'react'
import TobiCharacterIllustration from './TobiCharacterIllustration'
import AEtoLottiePipeline from './AEtoLottiePipeline'
import { useIsSSR } from '@/context/SSRContext'

const revealTransition = {
  duration: 0.6,
  ease: [0.16, 1, 0.3, 1] as const,
}

function NarrativeBlock({ children }: { children: React.ReactNode }) {
  const isSSR = useIsSSR()

  return (
    <motion.div
      initial={isSSR ? false : { opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={revealTransition}
      className="min-h-[50vh] flex items-start pt-8 lg:pt-16"
    >
      <div>{children}</div>
    </motion.div>
  )
}

function BlockFromStatic() {
  return (
    <NarrativeBlock>
      <span className="text-label text-accent block mb-2">01</span>
      <h3 className="text-h3 text-text-primary mb-4">From Static to Expressive</h3>
      <p className="text-body text-text-secondary mb-6">
        To increase conversation engagement, the static TOBi assistant was rigged intricately within
        Adobe After Effects to achieve a sophisticated faux-3D aesthetic with highly expressive
        motion. To solve performance degradation across varying device capabilities, these complex
        animations were exported as remarkably lightweight JSON files using the Lottie framework –
        ensuring rich character animations could be deployed across mobile, web, and rapid
        prototypes without battery drain or UI thread blocking.
      </p>
      <AEtoLottiePipeline />
    </NarrativeBlock>
  )
}

function EmotionVideo() {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    // Fix React's muted bug – must be set imperatively for iOS Safari
    video.muted = true

    const tryPlay = () => {
      video.play().catch(() => {})
    }

    // Re-trigger play when video enters the viewport (needed for scrollytelling on iOS)
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) tryPlay() },
      { threshold: 0.1 }
    )
    observer.observe(video)
    tryPlay()

    return () => observer.disconnect()
  }, [])

  return (
    <div className="rounded-xl overflow-hidden mt-6">
      <video
        ref={videoRef}
        className="w-full"
        src="/videos/tobi/tobi-emotions-tiled.mp4"
        autoPlay
        muted
        loop
        playsInline
      />
    </div>
  )
}

function BlockSystematizing() {
  return (
    <NarrativeBlock>
      <span className="text-label text-accent block mb-2">02</span>
      <h3 className="text-h3 text-text-primary mb-4">Systematizing Emotion</h3>
      <p className="text-body text-text-secondary mb-6">
        Engineered a vast emotional matrix of 28 distinct states covering the most common reactions
        the assistant requires. Every animation is exactly 2 or 4 seconds in duration, and –
        critically – every animation begins and ends on the exact same positional and rotational
        coordinates. This cyclical design allows developers to programmatically stitch different
        animations together in real-time, creating a continuous, unbroken performance.
      </p>
      <EmotionVideo />
    </NarrativeBlock>
  )
}

function BlockContextAware() {
  return (
    <NarrativeBlock>
      <span className="text-label text-accent block mb-2">03</span>
      <h3 className="text-h3 text-text-primary mb-4">Context-Aware Interactivity</h3>
      <p className="text-body text-text-secondary mb-4">
        TOBi doesn&apos;t move randomly – it reacts intelligently to the state of the product and
        the sentiment of user input. The system employs both UI triggers and Chat triggers.
      </p>
      <div className="mb-4">
        <p className="text-label text-text-muted uppercase tracking-wider mb-2">UI Triggers</p>
        <ul className="space-y-1">
          <li className="text-body-sm text-text-secondary">
            Repeated wrong password &rarr; &lsquo;Angry&rsquo; animation
          </li>
          <li className="text-body-sm text-text-secondary">
            Scroll beyond dashboard limits &rarr; &lsquo;Surprise&rsquo; easter egg
          </li>
        </ul>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          {
            video: '/videos/tobi/tobi-trigger-gamification.mp4',
            description: 'Scroll limit easter egg',
          },
          {
            video: '/videos/tobi/tobi-trigger-login.mp4',
            description: 'Wrong password reaction',
          },
          {
            video: '/videos/tobi/tobi-trigger-notification.mp4',
            description: 'Notification received',
          },
        ].map((trigger) => (
          <div key={trigger.video}>
            <div className="rounded-2xl overflow-hidden bg-surface-hover">
              <video
                className="w-full aspect-square object-cover"
                src={trigger.video}
                autoPlay
                muted
                loop
                playsInline
              />
            </div>
            <p className="text-body-sm text-text-secondary mt-3">{trigger.description}</p>
          </div>
        ))}
      </div>
    </NarrativeBlock>
  )
}

export default function TobiScrollytelling() {
  return (
    <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
      <div className="lg:w-1/2 lg:sticky lg:top-28 lg:self-start">
        <TobiCharacterIllustration />
      </div>

      <div className="lg:w-1/2">
        <BlockFromStatic />
        <BlockSystematizing />
        <BlockContextAware />
      </div>
    </div>
  )
}
